import { useEffect, useState } from 'react';
import {
  Combobox, useListCollection, Portal,
  ProgressCircle,
} from '@chakra-ui/react';
import type { FormErrors } from '@customTypes/errors';
import type { PortfolioType } from '@customTypes/portfolio';

type Props = {
  errors?: FormErrors | null;
  portfolios: PortfolioType[];
  onSearch: (value: string) => Promise<void>;
  value: PortfolioType | null;
  onChange: (portfolio: PortfolioType | null) => void;
}

const PortfolioSelect = ({
  errors, portfolios, onSearch, value, onChange,
}: Props) => {
  const [searchTimer, setSearchTimer] = useState<ReturnType<typeof setTimeout>>();
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);

  const { collection, set } = useListCollection({
    initialItems: portfolios,
    itemToValue: (item) => item.id,
    itemToString: (item) => item.name,
  })

  const updateSearch = (value:string) => {
    if (value !== search) {
      setSearching(true);
      setSearch(value);
      if (searchTimer) clearTimeout(searchTimer);
      setSearchTimer(setTimeout(async () => {
        await onSearch(value);
        setSearching(false);
      }, 1000));
    }
  };

  useEffect(() => {
    if (portfolios) {
      set(portfolios);
    }
  }, [portfolios, set]);

  return (
    <Combobox.Root
      collection={collection}
      value={value ? [value.id] : []}
      onValueChange={({ items }) => onChange(items[0] || null)}
      invalid={!!errors?.portfolio}
      variant="subtle"
    >
      <Combobox.Label>Portfólio</Combobox.Label>
      <Combobox.Control>
        <Combobox.Input placeholder="Pesquisar..." onChange={(e) => updateSearch(e.target.value)} />
        <Combobox.IndicatorGroup>
          {searching && (
            <ProgressCircle.Root value={null} size="xs">
              <ProgressCircle.Circle>
                <ProgressCircle.Track />
                <ProgressCircle.Range />
              </ProgressCircle.Circle>
            </ProgressCircle.Root>
          )}
          <Combobox.ClearTrigger />
          <Combobox.Trigger />
        </Combobox.IndicatorGroup>
      </Combobox.Control>
      <Portal>
        <Combobox.Positioner>
          <Combobox.Content zIndex="popover">
            <Combobox.Empty>Nenhum item encontrado</Combobox.Empty>
            {collection.items.map((item) => (
              <Combobox.Item item={item} key={item.id}>
                {item.name}
                <Combobox.ItemIndicator />
              </Combobox.Item>
            ))}
          </Combobox.Content>
        </Combobox.Positioner>
      </Portal>
    </Combobox.Root>
  );
};

export default PortfolioSelect;
